import type { RoomLayout, Questionnaire } from '../types.ts';

/**
 * Supported zone categories based on client specifications.
 */
export type ZoneType =
  | 'living'
  | 'dining'
  | 'kitchen'
  | 'workplace'
  | 'playground'
  | 'relax';

/**
 * Metadata definition for a zone category.
 */
export interface ZoneCategoryDefinition {
  id: string;              // Discriminator ID (e.g. 'living-id')
  type: ZoneType;          // Internal zone type ('living')
  nameCs: string;          // Czech display name ('Obývací zóna')
  nameEn: string;          // English display name ('Living Zone')
  descriptionCs: string;   // Czech description
  descriptionEn: string;   // English description
  typicalFurniture: string[]; // Key representative furniture items
  color: string;           // Translucent fill color for SVG
  strokeColor: string;     // Border stroke color for SVG
  badgeColor: string;      // Color for UI pills and badges
}

/**
 * Polygon Point [x, z] in millimeters relative to room center (0,0) as returned by Discriminator.
 */
export type Point2D = [number, number];

/**
 * Evaluated geometric zone partition.
 */
export interface Zone {
  id: string;              // Zone ID (e.g. 'living-id' or custom ID)
  type: ZoneType;          // Category type
  name: string;            // Display title (e.g. 'Obývací zóna')
  polygon: Point2D[];      // Outer ring vertices in mm relative to room center
  color: string;           // Translucent fill
  strokeColor: string;     // Border color
  badgeColor: string;
  area: number;            // Surface area in square meters (m²)
  areaPercent: number;     // Percentage of total room floor area (0-100%)
  centroid: [number, number]; // [x_mm, z_mm] relative to room center
  assignedObjectIds?: string[]; // IDs of furniture items placed inside this zone
}

/**
 * AI-generated spatial zone anchor object.
 */
export interface ZoneAnchorObject {
  id: string;
  name: string;
  zoneId: string;
  zoneType: ZoneType;
  position: {
    x: number; // in meters relative to room center (0,0) or bottom-left
    y: number; // in meters
    z: number; // in meters
  };
  size: {
    width: number;  // X in meters
    length: number; // Y in meters
    height: number; // Z in meters
  };
  rotationDegrees: number;
  isVirtualAnchor?: boolean;
}

/**
 * Request payload sent to the Zoning Service.
 */
export interface ZoningRequest {
  roomData: RoomLayout;
  prompt: string;
  questionnaire?: Questionnaire;
  model?: string;
  activeZoneTypes?: ZoneType[];
  customRulesMarkdown?: string;
}

/**
 * Response structure returned by the Zoning Service.
 */
export interface ZoningResult {
  success: boolean;
  zones: Zone[];
  updatedRoomData: RoomLayout;
  aiExplanation?: string;
  generatedAnchors?: ZoneAnchorObject[];
  executionTime?: number;
  logs?: Array<{
    stage: string;
    message: string;
    timestamp: string;
    data?: any;
  }>;
  error?: string;
}

/**
 * List of supported models for zoning AI reasoning.
 */
export const AVAILABLE_ZONING_MODELS = [
  { id: 'gemini-3.7-flash', name: 'Gemini 3.7 Flash (Default - Recommended)', description: 'Fastest & highest spatial reasoning accuracy' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: 'Lightweight & responsive' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'Complex multi-constraint reasoning' },
  { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', description: 'Legacy large context model' },
] as const;
